// ===========================================
// BUSTED! BLACKJACK - PIXI.JS AVATAR COUNTDOWN RING
// ===========================================
// Circular countdown ring around player avatars.
// Used for both betting and turn countdowns.
// Color transitions: Green -> Yellow -> Red as time runs out.
// ===========================================

import { Graphics } from 'pixi.js';

// ===========================================
// CONSTANTS
// ===========================================

// Countdown colors (matching PixiInsurancePanel)
const COUNTDOWN_GREEN = 0x22c55e;
const COUNTDOWN_YELLOW = 0xeab308;
const COUNTDOWN_RED = 0xef4444;

// Ring styling
const RING_STROKE_WIDTH = 6;
const RING_BACKGROUND_COLOR = 0x333333;
const RING_BACKGROUND_ALPHA = 0.5;

// Urgent state (< 3 seconds)
const URGENT_THRESHOLD_MS = 3000;
const URGENT_PULSE_SPEED = 0.15; // Pulse animation speed

// ===========================================
// PIXI AVATAR COUNTDOWN RING CLASS
// ===========================================

export class PixiAvatarCountdownRing extends Graphics {
  private deadline: number | null = null;
  private totalDuration: number = 10000; // Default 10 seconds
  private avatarRadius: number;
  private animationFrameId: number | null = null;
  private pulsePhase: number = 0;

  constructor(avatarRadius: number) {
    super();
    this.avatarRadius = avatarRadius;
    this.visible = false;
  }

  /**
   * Start countdown with deadline timestamp
   * @param deadline Unix timestamp when countdown expires
   * @param totalDuration Total duration in ms (for calculating progress)
   */
  public start(deadline: number, totalDuration: number = 10000): void {
    this.deadline = deadline;
    this.totalDuration = totalDuration;
    this.visible = true;
    this.pulsePhase = 0;

    // Start animation loop if not already running
    if (!this.animationFrameId) {
      this.animate();
    }
  }

  /**
   * Stop and hide the countdown ring
   */
  public stop(): void {
    this.deadline = null;
    this.visible = false;
    this.clear();

    // Stop animation loop
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Animation loop using requestAnimationFrame
   */
  private animate = (): void => {
    if (!this.visible || this.deadline === null) {
      this.animationFrameId = null;
      return;
    }

    this.update();
    this.animationFrameId = requestAnimationFrame(this.animate);
  };

  /**
   * Update avatar radius (for resize)
   */
  public setAvatarRadius(radius: number): void {
    this.avatarRadius = radius;
  }

  /**
   * Check if countdown is active
   */
  public isActive(): boolean {
    return this.deadline !== null && this.visible;
  }

  /**
   * Update callback (called every frame by ticker)
   */
  private update = (): void => {
    if (!this.visible || this.deadline === null) {
      return;
    }

    const now = Date.now();
    const remaining = Math.max(0, this.deadline - now);
    const progress = Math.min(1, remaining / this.totalDuration);

    // When expired, just draw empty ring (don't auto-stop - let external controller manage)
    // This prevents race conditions when transitioning between hands
    if (remaining <= 0) {
      this.drawRing(0, COUNTDOWN_RED, RING_STROKE_WIDTH, 1);
      return;
    }

    // Determine color based on time remaining
    let color = COUNTDOWN_GREEN;
    if (remaining < URGENT_THRESHOLD_MS) {
      color = COUNTDOWN_RED;
    } else if (remaining < 6000) {
      color = COUNTDOWN_YELLOW;
    }

    // Calculate pulse effect for urgent state
    let strokeWidth = RING_STROKE_WIDTH;
    let alpha = 1;
    if (remaining < URGENT_THRESHOLD_MS) {
      this.pulsePhase += URGENT_PULSE_SPEED;
      const pulse = Math.sin(this.pulsePhase) * 0.5 + 0.5; // 0 to 1
      strokeWidth = RING_STROKE_WIDTH + pulse * 2; // 6 to 8
      alpha = 0.7 + pulse * 0.3; // 0.7 to 1.0
    }

    // Draw the ring
    this.drawRing(progress, color, strokeWidth, alpha);
  };

  /**
   * Draw the countdown ring
   */
  private drawRing(progress: number, color: number, strokeWidth: number, alpha: number): void {
    this.clear();

    // Ring radius is slightly larger than avatar
    const ringRadius = this.avatarRadius + strokeWidth / 2 + 2;

    // Background circle (full ring, dimmed)
    this.circle(0, 0, ringRadius);
    this.stroke({ color: RING_BACKGROUND_COLOR, width: strokeWidth, alpha: RING_BACKGROUND_ALPHA });

    // Progress arc (starts from top, goes clockwise)
    if (progress > 0) {
      const startAngle = -Math.PI / 2; // Start from top
      const endAngle = startAngle + (2 * Math.PI * progress);

      this.arc(0, 0, ringRadius, startAngle, endAngle);
      this.stroke({ color, width: strokeWidth, alpha });
    }
  }

  /**
   * Cleanup - call when removing from scene
   */
  public dispose(): void {
    // Stop animation loop
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.destroy();
  }
}
